<template>
  <div class="container-fluid" id="income">
    <div class="row wrapper">
      <div class="col-12 col-md-4">
        <div class="header">
          <div id="steps" class="steps" tabindex="0" role="document">
            <span>Step 3</span> of 5
          </div>
          <h1 tabindex="0">Income and savings</h1>
          <div class="hero-image" alt="Income and savings"></div>
        </div>
      </div>
      <div class="col-12 col-md-8">
        <div class="main">
          <h2>Your income</h2>
          <ValidationObserver v-slot="{ valid }" ref="form">
            <form @submit.prevent="onSubmit">
              <div class="form-group">
                <div class="row no-gutters">
                  <div class="col-8">
                    <div v-if="data.stepData.salaryPeriod === 'Year'">
                      <ValidationProvider mode="lazy" name="salaryYearly" :rules="'required:'+pageContent.pages.income.salaryErrorYear+'|between:'+config.fields.salaryYearly.min+','+config.fields.salaryYearly.max+','+pageContent.pages.income.salaryErrorYear" v-slot="{ errors, failedRules }">
                        <label for="salaryYearly" tabindex="0">Your salary (before tax)</label>
                        <cleave
                          data-analytics-capture-value="true"
                          :options="formatOptions.moneys"
                          class="form-control"
                          id="salaryYearly"
                          name="salaryYearly"
                          v-model.number="data.stepData.salaryYearly"
                          v-on:blur="triggerHeightCheck"
                          type="text"
                          tabindex="0">
                        </cleave>
                        <span role="alert" class="form-text" v-bind:class="{ 'text-error': errors.length > 0 }" v-html="errors[0]"></span>
                        <span v-if="failedRules.between && pageContent.pages.income.salaryBoxError" v-html="pageContent.pages.income.salaryBoxError"></span>
                      </ValidationProvider>
                    </div>
                    <div v-if="data.stepData.salaryPeriod === 'Month'">
                      <ValidationProvider mode="lazy" name="salaryMonthly" :rules="'required:'+pageContent.pages.income.salaryErrorMonth+'|between:'+config.fields.salaryMonthly.min+','+config.fields.salaryMonthly.max+','+pageContent.pages.income.salaryErrorMonth" v-slot="{ errors, failedRules }">
                        <label for="salaryMonthly" tabindex="0">Your salary (before tax)</label>
                        <cleave
                          data-analytics-capture-value="true"
                          :options="formatOptions.moneys"
                          class="form-control"
                          id="salaryMonthly"
                          name="salaryMonthly"
                          v-model.number="data.stepData.salaryMonthly"
                          v-on:blur="triggerHeightCheck"
                          type="text"
                          tabindex="0">
                        </cleave>
                        <span role="alert" class="form-text" v-bind:class="{ 'text-error': errors.length > 0 }" v-html="errors[0]"></span>
                        <span v-if="failedRules.between && pageContent.pages.income.salaryBoxError" v-html="pageContent.pages.income.salaryBoxError"></span>
                      </ValidationProvider>
                    </div>
                    <div v-if="data.stepData.salaryPeriod === 'Week'">
                      <ValidationProvider mode="lazy" name="salaryWeekly" :rules="'required:'+pageContent.pages.income.salaryErrorWeek+'|between:'+config.fields.salaryWeekly.min+','+config.fields.salaryWeekly.max+','+pageContent.pages.income.salaryErrorWeek" v-slot="{ errors, failedRules }">
                        <label for="salaryWeekly" tabindex="0">Your salary (before tax)</label>
                        <cleave
                          data-analytics-capture-value="true"
                          :options="formatOptions.moneys"
                          class="form-control"
                          id="salaryWeekly"
                          name="salaryWeekly"
                          v-model.number="data.stepData.salaryWeekly"
                          v-on:blur="triggerHeightCheck"
                          type="text"
                          tabindex="0">
                        </cleave>
                        <span role="alert" class="form-text" v-bind:class="{ 'text-error': errors.length > 0 }" v-html="errors[0]"></span>
                        <span v-if="failedRules.between && pageContent.pages.income.salaryBoxError" v-html="pageContent.pages.income.salaryBoxError"></span>
                      </ValidationProvider>
                    </div>
                  </div>
                  <div class="col-4 pl-2">
                    <label for="salaryPeriod" tabindex="0">Per</label>
                    <select id="salaryPeriod" class="form-control" aria-label="Default select example" v-model="data.stepData.salaryPeriod" tabindex="0" @change="triggerHeightCheck()">
                      <option value="Year">Year</option>
                      <option value="Month">Month</option>
                      <option value="Week">Week</option>
                    </select>
                  </div>
                </div>
              </div>

              <h2 class="mt-5">Your savings</h2>

              <div class="form-group">
                <ValidationProvider mode="lazy" name="savings" :rules="'required:'+pageContent.pages.income.savingsError+'|between:'+config.fields.savings.min+','+config.fields.savings.max+','+pageContent.pages.income.savingsError" v-slot="{ errors }">
                  <label for="savings" tabindex="0">Total savings</label>
                  <cleave
                    data-analytics-capture-value="true"
                    :options="formatOptions.moneys"
                    class="form-control"
                    id="savings"
                    name="savings"
                    v-model.number="data.stepData.savings"
                    :value="data.stepData.savings"
                    v-on:blur="triggerHeightCheck"
                    type="text"
                    tabindex="0">
                  </cleave>
                  <span role="alert" class="form-text" v-bind:class="{ 'text-error': errors.length > 0 }" v-html="errors[0]"></span>
                </ValidationProvider>
              </div>

              <h2 class="mt-5">Your assets</h2>
              <p>{{pageContent.pages.income.yourAssets}}</p>

              <div class="form-group max">
                <div class="form-row">
                  <div class="col">
                    <label tabindex="0">Type of assets</label><br/>
                  </div>
                  <div class="col">
                    <label tabindex="0">Amount</label><br/>
                  </div>
                </div>

                <!-- assets -->
                <div class="form-row repeat-input" v-for="(asset, ind) in assets" :key="ind">
                  <div class="col">
                    <select class="form-control" aria-label="Default select example" v-model="asset.Name" tabindex="0">
                      <option selected disabled value="">Select asset</option>
                      <option v-for="(confAsset, ind) in config.assets" :key="ind" :value="confAsset.value">{{confAsset.label}}</option>
                    </select>
                  </div>
                  <div class="col">
                    <ValidationProvider mode="lazy" :name="'asset_'+ind" :rules="'required:'+pageContent.pages.income.assetRangeErrorMessage+'|between:'+config.fields.assets.min+','+config.fields.assets.max+','+pageContent.pages.income.assetRangeErrorMessage" v-slot="{ errors }">
                      <cleave
                        data-analytics-capture-value="true"
                        :class="{'is-muted': !asset.Name}"
                        :options="formatOptions.moneys"
                        class="form-control pre-remove-btn"
                        :id="'asset_'+ind"
                        v-model.number="asset.Amount"
                        v-on:blur="countAssets(); triggerHeightCheck()"
                        type="text"
                        :aria-label="'asset amount '+ind"
                        tabindex="0">
                      </cleave>
                      <button v-if="ind != 0" class="remove-item-btn" v-on:click="removeAsset(ind)" tabindex="0" type="button" role="button"><i class="icon-remove" aria-label="Remove asset"></i></button>
                      <br/><span role="alert" class="form-text" v-bind:class="{ 'text-error': errors.length > 0 }" v-html="errors[0]"></span>
                    </ValidationProvider>
                  </div>
                </div>

                <button class="add-button" v-on:click="addAsset" tabindex="0" type="button" role="button">+ Add asset</button>
                <hr>
                <span>Added assets</span>
                <h5>${{formatCurrency(totalAssetAmount, true)}}</h5>
                <span v-if="!totalAssetAmountValid" role="alert" class="form-text text-error" v-html="pageContent.pages.income.assetTotalErrorMessage"></span>
              </div>

              <div class="nav-button row">
                <router-link class="assumption-button col" :to="{ name: 'Assumptions', params: {activeTab: 'assumptions' } }" tabindex="0" role="button"><i class="icon-edit"></i> Edit assumptions </router-link>
                <router-link class="back-button col" to="/debt" tabindex="0" role="button">Back</router-link>
                <!-- <router-link class="next-button col" to="/expenses" tabindex="0" role="button">Next</router-link> -->
                <button class="next-button col" v-bind:class="[valid && totalAssetAmountValid ? 'is-active' : 'is-disabled']"  type="submit" tabindex="0" role="button">Next</button>
              </div>
            </form>
          </ValidationObserver>
        </div>
      </div>

    </div>
    <CustomFooter></CustomFooter>
  </div>
</template>


<script>
import { mapGetters } from 'vuex';
import { mapFields } from "vuex-map-fields";
import Cleave from "vue-cleave-component";

import { ValidationProvider, extend, ValidationObserver } from 'vee-validate';
import { required, between } from 'vee-validate/dist/rules';
import CustomFooter from '../components/CustomFooter';

import _ from 'lodash';

extend('required', {
  ...required,
  params: ['msg'],
  message: '{msg}'
});

extend('between', {
  ...between,
  params: ['min', 'max', 'msg'],
  validate(value, { min, max }) {
    return value >= min && value <= max;
  },
  message: '{msg}'
});

export default {
  name: 'Income',
  components: { Cleave, ValidationProvider, ValidationObserver, CustomFooter },
  computed: {
    ...mapFields(["data", "assets"]),
    ...mapGetters(["config", "formatOptions", "pageContent"]),
    totalAssetAmountValid() {
      return this.totalAssetAmount <= this.config.fields.assets.max;
    },
  },
  methods: {
    addAsset() {
      this.assets.push({"Name":'', "Amount": null, "Error": false});
      this.countAssets();
      this.triggerHeightCheck();
    },
    removeAsset(ind) {
      if(this.assets.length > 1) {
        this.assets.splice(ind, 1);
      }
      else {
        this.assets.pop();
        this.assets.push({"Name":'', "Amount": null, "Error": false});
      }
      this.countAssets();
      this.triggerHeightCheck();
    },
    countAssets() {
      this.totalAssetAmount = 0;
      this.assets.filter(a => a.Amount).forEach(asset => {
        this.totalAssetAmount += asset.Amount;
      });
    },
    formatCurrency: (value, noCents = false) => {
      return value === null
        ? ""
        : noCents
        ? value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")
        : parseFloat(value)
            .toFixed(2)
            .toString()
            .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    },
    triggerHeightCheck: function() {
      // Add slight delay otherwise incorrect height is read
      var rf = this;
      setTimeout(function() {
        rf.$refreshIframeHeight();
      }, 300);
    },
    onSubmit () {
      this.$refs.form.validateWithInfo().then(result => {
        if (!result.isValid) {
          var fieldArr = _.valuesIn(result.fields);
          var firstError = document.getElementById(fieldArr[0].id)
          if(firstError) {
            firstError.scrollIntoView({behavior: 'smooth'});
          }
          this.triggerHeightCheck();
          return;
        }
        if(!this.totalAssetAmountValid) return;
        this.$router.push('/expenses');
      });
    },
  },
  beforeDestroy() {
    // send analytics - complete form step
    var dataObj = {
      "salary": this.data.stepData.salaryYearly,
      "salaryPeriod": this.data.stepData.salaryPeriod,
      "savings": this.data.stepData.savings,
      "assets": []
    };
    if(this.data.stepData.salaryPeriod === "Month") {
      dataObj.salary = this.data.stepData.salaryMonthly;
    }
    else if(this.data.stepData.salaryPeriod === "Week") {
      dataObj.salary = this.data.stepData.salaryWeekly;
    }
    this.assets.forEach(asset => {
      var newAsset = {"Name": asset.Name, "Amount": asset.Amount};
      dataObj.assets.push(newAsset);
      this.$gtag.event("step-income", {
        "event_category": "field-data",
        "event_label": "asset: "+asset.Name,
        "value": asset.Amount,
      })
    });
    this.$gtag.event("step-income", {
      "event_category": "field-data",
      "event_label": "salary",
      "value": dataObj.salary,
    })
    this.$gtag.event("step-income", {
      "event_category": "field-data",
      "event_label": "salary period",
      "value": this.data.stepData.salaryPeriod
    })
    this.$gtag.event("step-income", {
      "event_category": "field-data",
      "event_label": "savings",
      "value": this.data.stepData.savings
    })

    // clear any asset errors when moving step. should not be any errors.
    // Was showing error if you went to next step and back to income step.
    this.assets.forEach(asset => {
      asset.Error = false;
    });
  },
  mounted() {
    this.triggerHeightCheck();
    this.$scrollIframeTop();
    window.addEventListener("resize", this.$refreshIframeHeight);
  },
  destroyed() {
    window.removeEventListener("resize", this.$refreshIframeHeight);
  },
  data() {
    return {
      totalAssetAmount: 0,
    };
  }
}


</script>
